'use client';

import useMediaQuery from '@jouzen/ecom-components/hooks/useMediaQuery';
import {
  InverseHeader,
  type InverseHeaderProps,
} from '@jouzen/ecom-components/layout';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';

type TargetViewport = 'mobile' | 'desktop' | 'both';

type InverseHeaderResponsiveProps = Omit<
  InverseHeaderProps,
  'enter' | 'exit'
> & {
  // When true, enables inverse on header enter for the enterTarget.
  readonly enterOn?: boolean;
  // When true, enables inverse on header exit for the exitTarget.
  readonly exitOn?: boolean;
  // Which viewport(s) should trigger enter (mobile, desktop, or both).
  readonly enterTarget?: TargetViewport;
  // Which viewport(s) should trigger exit (mobile, desktop, or both).
  readonly exitTarget?: TargetViewport;
  // Pixel breakpoint used for the default min-width media query.
  readonly breakpoint?: number;
  // Optional override for the media query string (takes priority over breakpoint).
  readonly mediaQuery?: string;
};

const matchesTarget = (target: TargetViewport, matches: boolean): boolean => {
  if (target === 'both') {
    return true;
  }

  if (target === 'desktop') {
    return matches;
  }

  return !matches;
};

// Wrapper to centralize responsive enter/exit inverse header behavior so
// call sites avoid repeating useMediaQuery logic.
// Enables applying InverseHeader based on browser size for finer control.
const InverseHeaderResponsive = ({
  children,
  enterOn = true,
  exitOn = false,
  enterTarget = 'mobile',
  exitTarget = 'mobile',
  breakpoint = BREAKPOINTS.large,
  mediaQuery,
  ...props
}: InverseHeaderResponsiveProps) => {
  const query = mediaQuery ?? `(min-width: ${breakpoint}px)`;
  const matches = Boolean(useMediaQuery(query));
  const isEnterTarget = matchesTarget(enterTarget, matches);
  const isExitTarget = matchesTarget(exitTarget, matches);

  const enterValue = Boolean(enterOn && isEnterTarget);
  const exitValue = Boolean(exitOn && isExitTarget);

  return (
    <InverseHeader enter={enterValue} exit={exitValue} {...props}>
      {children}
    </InverseHeader>
  );
};

export default InverseHeaderResponsive;
